import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

import { BreadcrumbSlotContext } from '@/shared/hooks/useBreadcrumbSlot';
import { cn } from '@/shared/lib/utils';

interface AppShellProps {
  /**
   * Barra lateral já montada. Quem decide quais itens existem — e quais o
   * papel da pessoa esconde — é `app/`, nunca este componente.
   */
  sidebar?: ReactNode;
  /**
   * Faixa superior, à direita da trilha de navegação: navegação compacta e
   * menu da pessoa no mobile. No desktop os dois vivem na sidebar e a faixa
   * fica só com a trilha.
   */
  topbar?: ReactNode;
  children: ReactNode;
  /**
   * A rota ocupa a altura da janela e **quem rola é o conteúdo dela**, não a
   * página (spec-calendar-ux.md §6.2, decisão 94).
   *
   * Existe para uma tela só, e por um motivo concreto: a grade do calendário
   * precisa de altura definida para que `expandRows` distribua as semanas por
   * igual e a célula tenha tamanho fixo. Numa página que rola, a grade não tem
   * de quem herdar altura — ela cresce com o conteúdo, e a linha do sábado
   * cheio estica.
   *
   * Some `max-width` e recheio vertical; `<main>` vira flex com `min-h-0`, que
   * é o que permite um filho rolar dentro dele em vez de empurrar a página.
   * **Vale só a partir de `lg`**: abaixo disso a rota volta a rolar
   * normalmente, porque duas colunas viram uma pilha (§2.2).
   */
  fullHeight?: boolean;
  className?: string;
}

/**
 * Moldura de todas as telas autenticadas: coluna fixa à esquerda, conteúdo
 * rolando à direita.
 *
 * Deliberadamente burro: não sabe quem está logado nem quais rotas existem.
 * `shared/` nunca importa de `features/` (ADR-006), e é isso que impede esta
 * moldura de virar o depósito de regra de acesso do sistema.
 *
 * As duas superfícies estão trocadas em relação ao padrão do shadcn/ui, de
 * propósito: a MESA acinzentada (`--background`) é a coluna lateral e o PAPEL
 * branco (`--card`) é a área de conteúdo. Quem lê o painel passa o dia na
 * área de conteúdo — ela é que merece a superfície limpa; a navegação recua.
 *
 * A sidebar é `hidden md:flex`: abaixo de 768px ela sai do fluxo e a
 * navegação vive na faixa superior. Um menu deslizante seria mais elegante e
 * traria estado de abertura, foco preso e overlay para manter — com três
 * itens de navegação não paga o custo (RNF-U02 pede utilizável a 375px, não
 * sofisticado).
 */
export function AppShell({
  sidebar,
  topbar,
  children,
  fullHeight = false,
  className,
}: AppShellProps) {
  // `useState` e não `useRef`: o nó só existe depois da primeira pintura, e o
  // `PageHeader` precisa re-renderizar quando ele aparecer para portar a
  // trilha para dentro dele.
  const [breadcrumbSlot, setBreadcrumbSlot] = useState<HTMLElement | null>(null);

  const mainRef = usePageTransition();

  return (
    <BreadcrumbSlotContext.Provider value={breadcrumbSlot}>
      <div className="min-h-screen bg-background text-foreground">
        <a
          href="#conteudo"
          className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-primary-foreground"
        >
          Pular para o conteúdo
        </a>

        <div className={cn('flex min-h-screen', fullHeight && 'lg:h-screen lg:min-h-0')}>
          {sidebar ? (
            /**
             * O `z-40` é o que faz o modo `hover` da sidebar **sobrepor de
             * verdade**, e ele precisa morar AQUI, não lá dentro.
             *
             * `position: sticky` cria contexto de empilhamento mesmo com
             * `z-index: auto` — então o `z-40` do `<aside>` só valia contra os
             * irmãos dele, nunca contra esta árvore. Do lado de fora a coluna
             * competia como nível 0, e perdia duas vezes: para o `<header>`
             * (`z-30`) e para o `<main>`, que a `animate-page-in` deixa com
             * `transform` residual (`both` mantém o `translateY(0)`) — outro
             * contexto de empilhamento, nível 0, e POSTERIOR na ordem do
             * documento. Resultado: a coluna expandida abria por baixo do
             * conteúdo, com os dois textos sobrepostos.
             *
             * Fica abaixo do `z-50` da `Sheet` e do `DayContextMenu` de
             * propósito: os dois vão para `document.body` e devem cobrir a
             * navegação, não o contrário.
             */
            <div className="sticky top-0 z-40 hidden h-screen shrink-0 md:flex">{sidebar}</div>
          ) : null}

          <div className={cn('flex min-w-0 flex-1 flex-col bg-card', fullHeight && 'lg:min-h-0')}>
            <header className="sticky top-0 z-30 border-b border-border bg-card/80 backdrop-blur-md">
              <div className="flex h-14 items-center gap-3 px-4 sm:px-6">
                <div ref={setBreadcrumbSlot} className="flex min-w-0 flex-1 items-center" />
                {topbar}
              </div>
            </header>

            <main
              ref={mainRef}
              id="conteudo"
              className={cn(
                'w-full flex-1 animate-page-in px-4 sm:px-6',
                fullHeight
                  ? // `min-h-0` é o que faz o filho rolar em vez de esticar o
                    // `<main>`; sem ele, `overflow-y-auto` lá dentro não tem
                    // limite nenhum contra o qual rolar.
                    'py-6 sm:py-8 lg:flex lg:min-h-0 lg:flex-col lg:py-4'
                  : 'mx-auto max-w-6xl py-6 sm:py-8',
                className,
              )}
            >
              {children}
            </main>
          </div>
        </div>
      </div>
    </BreadcrumbSlotContext.Provider>
  );
}

/**
 * A área de conteúdo entra de baixo a cada troca de tela.
 *
 * **Reinicia a animação sem remontar nada.** Um `key={pathname}` no `<main>`
 * seria uma linha em vez de dez — e destruiria a árvore inteira a cada
 * navegação, o que no formulário de projeto significa perder o que a pessoa
 * está digitando. Remover a classe, forçar reflow e recolocá-la roda a mesma
 * animação sem tocar em estado de componente nenhum.
 *
 * **`REPLACE` não anima, e é aqui que mora a sutileza.** Nem toda mudança de
 * URL é uma navegação que a pessoa pediu: quando o rascunho nasce no primeiro
 * campo preenchido, `/calendario/novo` vira `/calendario/:id/editar` por `replace`
 * (ver `ProjectFormPage`). Animar aí faria a tela inteira piscar embaixo de
 * quem está no meio de uma frase — leria como bug, não como acabamento. Filtro
 * na URL (`?situacao=`) também usa `replace`, e também não deve reanimar a
 * tela: quem muda um filtro está olhando a LISTA, e é ela que responde.
 */
function usePageTransition() {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const mainRef = useRef<HTMLElement>(null);
  // A primeira pintura já entra animada pela classe no JSX; reiniciar aqui
  // rodaria a mesma animação duas vezes.
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (navigationType === 'REPLACE') return;

    const node = mainRef.current;
    if (!node) return;

    /**
     * Volta ao topo ANTES de animar, e é metade do conserto do "susto".
     *
     * O React Router não mexe na rolagem. Sem isto, sair do meio de uma lista
     * longa pela trilha de navegação abre a tela nova na MESMA altura — a
     * pessoa cai no miolo de um conteúdo que nunca viu, com o título fora da
     * vista. Nenhuma animação disfarça isso, porque o problema não é a
     * transição: é o destino estar no lugar errado.
     *
     * `instant` de propósito, apesar do `scroll-behavior: smooth` global: a
     * rolagem suave aqui competiria com a animação de entrada, e as duas juntas
     * dariam a impressão de a tela escorregar duas vezes. Voltar em `POP` seria
     * pior — quem clica em "voltar" quer reencontrar a lista onde parou.
     */
    if (navigationType === 'PUSH') {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }

    node.classList.remove('animate-page-in');
    node.getBoundingClientRect(); // força o reflow que rearma a animação
    node.classList.add('animate-page-in');
  }, [pathname, navigationType]);

  return mainRef;
}
